import type { SseTabMessage, TabLeaderHandle } from '@/lib/sse-tab-leader'
import { createResilientEventSource } from '@/lib/resilient-event-source'
import { createTabLeader } from '@/lib/sse-tab-leader'

/**
 * One SSE connection per URL per browser, shared by every hook that wants
 * events. Cross-tab sharing lives in the tab leader; this registry is the
 * per-tab half: hooks subscribe here and get events by name.
 *
 * The server pushes EVERY event type of a user into EVERY connection, and the
 * server caps connections per user (evicting the oldest). Before this registry
 * each hook opened its own EventSource to the same URL, so a single browser
 * could hold more connections than the cap and evict itself in a loop.
 *
 * Because `EventSource` only dispatches names that were registered, the one
 * shared connection listens for the union of every name the app consumes
 * (`SSE_EVENT_NAMES`) and fans each event out by name to the subscribers that
 * asked for it.
 */

type EventHandler = (ev: MessageEvent<unknown>) => void

/**
 * Every named SSE event the app consumes, i.e. the union of the listener keys
 * of `use-sse-notifications`, `use-station-sse` and `use-customer-display-sse`.
 * A hook listening for a name missing here would never fire.
 *
 * `heartbeat` and `auth_expired` are deliberately absent: the resilient
 * wrapper owns those and must stay the only listener for them.
 */
export const SSE_EVENT_NAMES = [
    'notification',
    'customer_import_complete',
    'product_import_complete',
    'order_start_delivery',
    'order_delivery_success',
    'order_delivery_failed',
    'order_stock_out_done',
    'order_payment_received',
    'station_new_order',
    'station_bank_in',
    'station_stock_low',
    'station_machine_finishing',
    'station_packaging_log',
    'station_lot_debt',
    'station_work_activity',
    // Sổ 12,5g: phiếu của một mẻ vừa duyệt → tải lại để hiện nút in tem (M-87).
    'packaging_log_note_approved',
    'customer_display_qr',
    'customer_display_paid',
    'customer_display_cart',
    'customer_display_checkout',
    'customer_display_clear',
] as const

export type SseEventName = (typeof SSE_EVENT_NAMES)[number]
export type SseListeners = Partial<Record<SseEventName, EventHandler>>

export interface SseSubscription {
    url: string
    /** Named SSE events this subscriber wants. Only registered names can be subscribed. */
    listeners: SseListeners
    onConnectedChange?: (connected: boolean) => void
}

// A subscriber leaving is usually a re-render, not a goodbye: React StrictMode
// and HMR unmount then remount within the same tick. Closing immediately would
// drop and rebuild the connection every time.
const CLOSE_GRACE_MS = 250

interface StreamEntry {
    handle: TabLeaderHandle | null
    subscribers: Set<SseSubscription>
    connected: boolean
    closeTimer: ReturnType<typeof setTimeout> | null
}

const entries = new Map<string, StreamEntry>()

/**
 * The one path into the subscribers of this tab. A message reaches it either
 * from the connection this tab owns or, in a follower tab, from the leader
 * over the broadcast channel — subscribers cannot tell the two apart.
 */
function deliver(entry: StreamEntry, message: SseTabMessage) {
    if (message.kind === 'state') {
        entry.connected = message.connected
        // Copy: a handler may unsubscribe while the fan-out is running.
        for (const subscriber of [...entry.subscribers]) {
            subscriber.onConnectedChange?.(message.connected)
        }
        return
    }
    const eventName = SSE_EVENT_NAMES.find((name) => name === message.type)
    if (!eventName) return
    const event = new MessageEvent<unknown>(eventName, { data: message.data })
    for (const subscriber of [...entry.subscribers]) {
        subscriber.listeners[eventName]?.(event)
    }
}

function openStream(url: string, entry: StreamEntry) {
    return createTabLeader({
        key: `erp-sse:${url}`,
        openStream: (publish) => {
            const listeners: SseListeners = {}
            for (const name of SSE_EVENT_NAMES) {
                listeners[name] = (event) => {
                    publish({
                        kind: 'event',
                        type: name,
                        data: String(event.data),
                    })
                }
            }
            return createResilientEventSource({
                url,
                listeners,
                onConnectedChange: (connected) =>
                    publish({ kind: 'state', connected }),
            })
        },
        onMessage: (message) => deliver(entry, message),
    })
}

function closeEntry(url: string, entry: StreamEntry) {
    // A newer entry for the same URL means this one was already retired.
    if (entries.get(url) !== entry) return
    entry.closeTimer = null
    if (entry.subscribers.size > 0) return
    entries.delete(url)
    entry.handle?.close()
    entry.handle = null
    entry.connected = false
}

/**
 * Subscribe to the shared stream for `url`, opening it if this is the first
 * subscriber. Returns an idempotent unsubscribe.
 */
export function subscribeSse(subscription: SseSubscription): () => void {
    const { url } = subscription
    let entry = entries.get(url)
    if (!entry) {
        entry = {
            handle: null,
            subscribers: new Set(),
            connected: false,
            closeTimer: null,
        }
        entries.set(url, entry)
    }
    const current = entry
    if (current.closeTimer) {
        clearTimeout(current.closeTimer)
        current.closeTimer = null
    }
    current.subscribers.add(subscription)
    // Late subscribers must learn the state they joined, not wait for a change.
    subscription.onConnectedChange?.(current.connected)
    current.handle ??= openStream(url, current)

    let released = false
    return () => {
        if (released) return
        released = true
        current.subscribers.delete(subscription)
        if (current.subscribers.size > 0) return
        current.closeTimer = setTimeout(
            () => closeEntry(url, current),
            CLOSE_GRACE_MS,
        )
    }
}
